import type { AnnualReturnRow } from 'stores/annual-returns-store';
import { ANNUAL_RETURN_TYPE } from 'src/constants/annualReturnType';
import { resolveOrganisationName } from 'src/utils';
import { useRegionLookup } from '../useRegionLookup';

/**
 * Resolves the plain-language payee and short name for a return from its
 * organisation/region relation: a regional return is paid to its region, an
 * organisation return to the organisation (UGLE / SGC).
 *
 * Store-backed, so it lives as a composable rather than a pure util — the region
 * name is resolved from the client-side regions dictionary by `region_id`, and
 * the organisation name from the organisation constant by `organisation_id`,
 * rather than from objects embedded on the return.
 */
export function useAnnualReturnPayee() {
  const { getRegion } = useRegionLookup();

  function resolvePayee(row: AnnualReturnRow): {
    shortName: string;
    payee: string;
  } {
    if (row.type === ANNUAL_RETURN_TYPE.REGION) {
      const region = getRegion(row.region_id);
      return {
        shortName: region?.region_short_name || region?.region_name || 'Region',
        payee: region?.region_name || 'your region',
      };
    }

    return {
      shortName: resolveOrganisationName(row.organisation_id) || 'Organisation',
      payee:
        resolveOrganisationName(row.organisation_id, true) ||
        resolveOrganisationName(row.organisation_id) ||
        'the organisation',
    };
  }

  return { resolvePayee };
}
